import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { hasModuleAccess, useAuth } from "../context/AuthContext";
import Logo from "./Logo";
import {
  House,
  Kanban,
  Users,
  Calendar,
  Receipt,
  Bell,
  SignOut,
  List,
  X,
  Tooth,
  Stethoscope,
  Gear,
  CalendarBlank,
  Stack,
  ChartBar,
  ShieldCheck,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/staff", label: "Dashboard", icon: House, end: true, module: "dashboard" },
  { to: "/staff/calendar", label: "Calendar", icon: CalendarBlank, module: "calendar" },
  { to: "/staff/enquiries", label: "Enquiries", icon: Kanban, module: "enquiries" },
  { to: "/staff/patients", label: "Patients", icon: Users, module: "patients" },
  { to: "/staff/appointments", label: "Appointments", icon: Calendar, module: "appointments" },
  { to: "/staff/reports", label: "Reports", icon: ChartBar, module: "reports" },
  { to: "/staff/treatments", label: "Treatments", icon: Tooth, module: "treatments" },
  { to: "/staff/invoices", label: "Invoices", icon: Receipt, module: "invoices" },
  { to: "/staff/reminders", label: "Reminders", icon: Bell, module: "reminders" },
  { to: "/staff/templates", label: "Plan Templates", icon: Stack, adminOnly: true, module: "templates" },
  { to: "/staff/staff-roles", label: "Staff Roles", icon: ShieldCheck, adminOnly: true, module: "staff_roles" },
  { to: "/staff/doctors", label: "Doctors", icon: Stethoscope, adminOnly: true, module: "doctors" },
  { to: "/staff/settings", label: "Settings", icon: Gear, adminOnly: true, module: "settings" },
];

export default function StaffLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-72 bg-[#0A0A0A] text-white p-6 flex flex-col gap-6 transform transition-transform ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
        data-testid="staff-sidebar"
      >
        <div className="flex items-center justify-between">
          <Logo size={42} withText dark />
          <button
            className="lg:hidden text-white"
            onClick={() => setOpen(false)}
            data-testid="sidebar-close"
          >
            <X size={22} />
          </button>
        </div>

        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-2">
          {user?.role === "admin" ? "Admin Console" : user?.role === "doctor" ? "Doctor Console" : "Staff Console"}
        </div>

        <nav className="flex flex-col gap-1">
          {NAV.filter((n) => (!n.adminOnly || user?.role === "admin") && (!n.module || hasModuleAccess(user, n.module))).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-[#E60000] text-white font-semibold"
                    : "text-zinc-300 hover:text-white hover:bg-zinc-900"
                }`
              }
              data-testid={`nav-${n.label.toLowerCase()}`}
            >
              <n.icon size={18} weight="bold" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto pt-6 border-t border-zinc-900">
          <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
          <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
          <button
            onClick={doLogout}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-semibold rounded-sm py-2 transition-colors"
            data-testid="logout-button"
          >
            <SignOut size={16} weight="bold" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile topbar */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden bg-white border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setOpen(true)} data-testid="sidebar-open">
            <List size={22} />
          </button>
          <Logo size={32} />
          <div className="w-6" />
        </header>

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>

      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="border-b border-zinc-200 bg-white">
      <div className="px-6 sm:px-10 py-6 sm:py-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
            Dr Tayyeb&apos;s CRM
          </div>
          <h1
            className="text-3xl sm:text-4xl font-black tracking-tighter text-zinc-900"
            style={{ fontFamily: "Manrope, sans-serif" }}
            data-testid="page-title"
          >
            {title}
          </h1>
          {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}
